import _ from "lodash";
import {
  LABEL_CONFIG,
  TEXT_ELEMENT_CONFIG,
  TRANFORMER_CONFIG,
} from "../KonvaConfig";
import {
  CANVAS_IDS,
  KONVA_ATTR,
  KONVA_EVENTS,
  KONVA_ITEM_TYPE,
} from "../KonvaConstants";
import {
  isTextRTL,
  generateElementName,
  getCanvasRelativeSize,
} from "../../KonvaCommonUtils";

// * feature variable
let selectedLabelNode = null;

/**
 *
 * @param {*} item
 * @param {*} params
 * @returns Label node for labelling question type
 */
export const createLabelNode = (
  item,
  { mode, layer, stage, konva } = {},
  t = () => {}
) => {
  const id = _.get(item, "id");
  const x = _.get(item, "x", 0);
  const y = _.get(item, "y", 0);
  const text = _.get(item, "text", "");

  const isRTL = isTextRTL(text);

  const fontSize = getCanvasRelativeSize(16, false, false);
  stage.setAttr(`${id}#${KONVA_ATTR.LABEL_ARROWS}`, _.get(item, "arrows", []));
  const group = new konva.Group({ id: `${id}_${CANVAS_IDS.LABEL_GROUP}` });
  layer.add(group);

  // * Text element for label
  const labelText = new konva.Text({
    id,
    x,
    y,
    text: _.isEmpty(text)
      ? t(mode === "create" ? "common:add_with_label" : "common:typeHere", {
          label: t("worksheet:answer"),
        })
      : text,
    fill:
      mode === "preview" && text !== t("common:typeHere") && !_.isEmpty(text)
        ? TEXT_ELEMENT_CONFIG.color
        : _.get(item, "isPlaceholder", false)
        ? TEXT_ELEMENT_CONFIG.PLACEHOLDER_FILL
        : TEXT_ELEMENT_CONFIG.color,
    fontSize,
    name: generateElementName(item),
    fontStyle: TEXT_ELEMENT_CONFIG.fontStyle,
    fontFamily: TEXT_ELEMENT_CONFIG.fontFamily,
    draggable: _.get(item, "draggable", false),
    padding: getCanvasRelativeSize(12, false, false),
    lineHeight: 1,
    verticalAlign: "middle",
    offsetX: 0,
    offsetY: -1,
    wrap: "none",
    ellipsis: true,
    align: isRTL ? "right" : "left",
    direction: isRTL ? "rtl" : "ltr",
  });

  const labelTextWidth = getCanvasRelativeSize(150, false, false);
  labelText.width(labelTextWidth);

  // * Label element container with anchor and box-shadow
  const labelContainer = new konva.Rect({
    id: `${id}_${CANVAS_IDS.TEXT_WRAPPER_ID}`,
    x: labelText.x(),
    y: labelText.y(),
    width: labelText.width(),
    height: labelText.height(),
    fill: TEXT_ELEMENT_CONFIG.WRAPPER_FILL,
    stroke: TEXT_ELEMENT_CONFIG.WRAPPER_STROKE,
    strokeWidth: getCanvasRelativeSize(
      TEXT_ELEMENT_CONFIG.WRAPPER_STROKE_WIDTH
    ),
    cornerRadius: TEXT_ELEMENT_CONFIG.WRAPPER_CORNER_RADIUS,
    shadowColor: TEXT_ELEMENT_CONFIG.WRAPPER_SHADOW,
    shadowBlur: TEXT_ELEMENT_CONFIG.WRAPPER_SHADOW_BLUR,
    shadowOffsetY: TEXT_ELEMENT_CONFIG.WRAPPER_SHADOW_OFFSETY,
    listening: false,
    draggable: false,
  });
  drawLabelNodeArrows(labelText, item, { konva, group });
  group.add(labelContainer);

  group.add(labelText);

  if (mode === "create") {
    // * adding anchor on for sides of label and keeping hidden as default
    addLabelAnchorPointers(labelText, { konva, stage });

    // * Moving label container and anchor with label text to keep in synck
    labelText.on(KONVA_EVENTS.DRAGMOVE, () => {
      labelContainer.x(labelText.x());
      labelContainer.y(labelText.y());
      updateLabelAnchorAndArrowPosition(labelText, stage);
    });
  }

  return group;
};

/**
 *
 * @param {*} node
 * @param {*} stage
 * @description update anchor position on moving label text
 */
const updateLabelAnchorAndArrowPosition = (node, stage) => {
  const id = node.id();
  const x = node.x();
  const y = node.y();
  const width = node.width();
  const height = node.height();
  const labelAnchorPayload = getLabelAnchorPayload(id, { x, y, height, width });

  // * Update anchor position on dragging label node
  _.forEach(labelAnchorPayload, item => {
    const element = stage.findOne(`#${item.id}`);
    if (element) {
      element.x(item.x);
      element.y(item.y);
    }
  });

  // * Update arrow position on dragging label node
  const arrows = stage.getAttr(`${id}#${KONVA_ATTR.LABEL_ARROWS}`) || [];
  _.forEach(arrows, arrow => {
    const element = stage.findOne(`#${_.get(arrow, "id")}`);
    const supportElementDash = stage.findOne(
      `#${_.get(arrow, "id")}-dash-support`
    );
    if (element) {
      const startPoint = _.find(labelAnchorPayload, anchor => {
        return _.includes(_.get(anchor, "id"), _.get(arrow, "from"));
      });
      if (startPoint) {
        element.points([
          _.get(startPoint, "x", 0),
          _.get(startPoint, "y", 0),
          ..._.get(arrow, "to", []),
        ]);
        if (supportElementDash) {
          supportElementDash.points([
            _.get(startPoint, "x", 0),
            _.get(startPoint, "y", 0),
            ..._.get(arrow, "to", []),
          ]);
        }
      }
    }
  });
};

/**
 *
 * @param {*} node
 * @param {*} param1
 * @returns Adding anchor circles on label
 */
export const addLabelAnchorPointers = (node, { konva, stage }) => {
  if (!node) return;
  const id = node.id();
  const x = node.x();
  const y = node.y();
  const width = node.width();
  const height = node.height();

  const labelGroup = stage.findOne(`#${id}_${CANVAS_IDS.LABEL_GROUP}`);
  if (!labelGroup) return;

  const labelAnchorPayload = getLabelAnchorPayload(id, { x, y, height, width });

  // * creating all four side anchor and adding in label group
  return _.map(labelAnchorPayload, item => {
    const element = new konva.Circle({
      id: _.get(item, "id"),
      x: _.get(item, "x", 0),
      y: _.get(item, "y", 0),
      name: _.get(item, "id"),
      radius: getCanvasRelativeSize(4),
      strokeWidth: getCanvasRelativeSize(1),
      stroke: TRANFORMER_CONFIG.STROKE,
      fill: TRANFORMER_CONFIG.ANCHORFILL,
      listening: true,
      draggable: true,
      visible: false,
    });
    labelGroup.add(element);
    return element;
  });
};

/**
 *
 * @param {*} node
 * @param {*} konva
 * @returns function to determin is it label node
 */
export const isLabelNode = node => {
  if (!node || !_.isFunction(node?.name)) return false;

  const name = _.split(node.name(), "#");
  return _.first(name) === KONVA_ITEM_TYPE.LABEL;
};

/**
 *
 * @param {*} node
 * @param {*} visible
 * @description updating anchor visibility true/false
 */
export const updateAnchorVisibility = (id, stage, visible = false) => {
  _.forEach(getLabelAnchorPayload(id, {}), item => {
    const element = stage.findOne(`#${item.id}`);
    if (element) {
      element.visible(visible);
    }
  });
};

/**
 *
 * @param {*} node
 * @param {*} stage
 * @returns Update label element on transformer attached
 */
export const onTransformerAttachedToLabel = (node, stage) => {
  onTransformerDetachedToLabel(stage);
  if (!node) return;

  const id = node.id();
  const labelContainer = stage.findOne(`#${id}_${CANVAS_IDS.TEXT_WRAPPER_ID}`);

  if (!labelContainer) return;
  labelContainer.stroke(TRANFORMER_CONFIG.STROKE);
  selectedLabelNode = id;
  updateLabelAnchorAndArrowPosition(node, stage);
  updateAnchorVisibility(id, stage, true);
};

/**
 *
 * @param {*} node
 * @param {*} stage
 * @returns Update label element on transformer detached
 */
export const onTransformerDetachedToLabel = (stage, node, transfomer) => {
  const isTextEditMode =
    node &&
    stage.getAttr(KONVA_ATTR.IS_CHANGING_TEXT) === node.id() &&
    isLabelNode(node);

  if (!selectedLabelNode && !isTextEditMode) return;

  const id = isTextEditMode ? node.id() : selectedLabelNode;
  const labelContainer = stage.findOne(`#${id}_${CANVAS_IDS.TEXT_WRAPPER_ID}`);
  if (!labelContainer) return;

  updateAnchorVisibility(id, stage, false);

  if (!isTextEditMode) {
    labelContainer.stroke(TEXT_ELEMENT_CONFIG.WRAPPER_STROKE);
  } else if (transfomer) {
    transfomer.visible(false);
  }

  selectedLabelNode = null;
};

/**
 *
 * @param {*} id
 * @param {*} param
 * @returns Anchor circle payload's
 */
export const getLabelAnchorPayload = (
  id,
  { x = 0, y = 0, width = 0, height = 0 } = {}
) => [
  {
    id: `${id}_${CANVAS_IDS.LABEL_ANCHOR.TOP}`,
    x: x + width / 2,
    y: y,
  },
  {
    id: `${id}_${CANVAS_IDS.LABEL_ANCHOR.RIGHT}`,
    x: x + width,
    y: y + height / 2,
  },
  {
    id: `${id}_${CANVAS_IDS.LABEL_ANCHOR.BOTTOM}`,
    x: x + width / 2,
    y: y + height,
  },
  {
    id: `${id}_${CANVAS_IDS.LABEL_ANCHOR.LEFT}`,
    x: x,
    y: y + height / 2,
  },
];

/**
 *
 * @param {*} node
 * @param {*} item
 * @param {*} params
 * @returns Draw label arrows
 */
const drawLabelNodeArrows = (node, item, { konva, group }) => {
  if (!node) return;

  const id = node.id();
  const x = node.x();
  const y = node.y();
  const width = node.width();
  const height = node.height();
  const labelAnchorPayload = getLabelAnchorPayload(id, { x, y, height, width });

  const arrows = _.get(item, "arrows", []);
  const strokeWidth = getCanvasRelativeSize(
    LABEL_CONFIG.ARROW_STROKE_WIDTH,
    false,
    false
  );

  return _.map(arrows, arrow => {
    const startPoint = _.find(labelAnchorPayload, anchor => {
      return _.includes(_.get(anchor, "id"), _.get(arrow, "from"));
    });
    if (!startPoint) return;
    const payload = {
      id: _.get(arrow, "id"),
      name: `${_.get(arrow, "id")}#${_.get(item, "id")}#${
        CANVAS_IDS.LABEL_ARROW
      }`,
      pointerLength: getCanvasRelativeSize(8, false, false),
      pointerWidth: getCanvasRelativeSize(8, false, false),
      fill: "black",
      stroke: "black",
      strokeWidth: strokeWidth,
      dash: [strokeWidth, strokeWidth],
      hitStrokeWidth: Math.min(strokeWidth * 9, 20),
      points: [
        _.get(startPoint, "x", 0),
        _.get(startPoint, "y", 0),
        ..._.get(arrow, "to", []),
      ],
    };
    const arrowNode = new konva.Arrow(payload);

    const supportPayload = {
      ...payload,
      stroke: "white",
      id: `${_.get(arrow, "id")}-dash-support`,
      name: `${_.get(arrow, "id")}#${_.get(item, "id")}#SUPPORTARROW`,
      listening: false,
      draggable: false,
    };
    delete supportPayload.dash;
    const supportArrowNode = new konva.Arrow(supportPayload);
    group.add(supportArrowNode);
    group.add(arrowNode);
    return arrowNode;
  });
};
